import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { CheckCircle2, XCircle, ExternalLink, Filter, Star, TrendingUp, Clock, DollarSign, Award, Sparkles } from "lucide-react";

/**
 * Prop-firm comparator — public page (SEO magnet + potential affiliate revenue).
 * Data is a static curated list; affiliate slugs left as placeholders (?ref=ANATRADEX).
 * User will replace with real IDs after signing up to each firm's affiliate program.
 */

// Curated data (sanitized – always verify on the firm's own site before purchase).
const FIRMS = [
  {
    id: "ftmo",
    name: "FTMO",
    flag: "🇨🇿",
    website: "https://ftmo.com/?ref=ANATRADEX",
    price100k: 540,
    steps: "2 étapes",
    maxDD: 10,
    dailyDD: 5,
    profitTarget: [10, 5],
    split: "80–90%",
    payoutDays: 14,
    platforms: ["MT4", "MT5", "cTrader", "DXtrade"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Réputation solide", "Payouts fiables", "Communauté active"],
    weaknesses: ["Prix plus élevé", "2 étapes obligatoires"],
    bestFor: ["swing", "day", "prop"],
    rating: 4.7,
  },
  {
    id: "fundednext",
    name: "FundedNext",
    flag: "🌐",
    website: "https://fundednext.com/?ref=ANATRADEX",
    price100k: 549,
    steps: "1 ou 2 étapes",
    maxDD: 10,
    dailyDD: 5,
    profitTarget: [10, 5],
    split: "80–95%",
    payoutDays: 7,
    platforms: ["MT4", "MT5", "cTrader"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Payouts hebdomadaires rapides", "Programme Stellar 1-step disponible", "Split jusqu'à 95%"],
    weaknesses: ["Moins mature qu'FTMO", "Règles évolutives"],
    bestFor: ["day", "scalp", "prop"],
    rating: 4.5,
  },
  {
    id: "the5ers",
    name: "The 5%ers",
    flag: "5️⃣",
    website: "https://the5ers.com/?ref=ANATRADEX",
    price100k: 260,
    steps: "1 étape (Bootcamp)",
    maxDD: 4,
    dailyDD: 4,
    profitTarget: [6, 6],
    split: "80–100%",
    payoutDays: 30,
    platforms: ["MT4", "MT5"],
    consistency: true,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Programme scaling agressif jusqu'à 100%", "Historique très long (2016)", "Sérieux et régulé"],
    weaknesses: ["Drawdown de 4% très strict", "Consistency rule obligatoire"],
    bestFor: ["swing", "long_term"],
    rating: 4.4,
  },
  {
    id: "myforexfunds",
    name: "MyForexFunds",
    flag: "💱",
    website: "https://myforexfunds.com/?ref=ANATRADEX",
    price100k: 599,
    steps: "1 ou 2 étapes",
    maxDD: 8,
    dailyDD: 5,
    profitTarget: [8, 5],
    split: "75–90%",
    payoutDays: 14,
    platforms: ["MT4", "MT5"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Rapid: pas de min trading days", "Split croissant"],
    weaknesses: ["Historique récent avec des controverses juridiques 2023"],
    bestFor: ["scalp", "day"],
    rating: 3.9,
  },
  {
    id: "topstep",
    name: "TopStep",
    flag: "🎯",
    website: "https://topstep.com/?ref=ANATRADEX",
    price100k: 165,
    steps: "1 étape",
    maxDD: 3,
    dailyDD: 2,
    profitTarget: [6, 0],
    split: "90–100%",
    payoutDays: 8,
    platforms: ["NinjaTrader", "TradingView"],
    consistency: false,
    weekendHolding: false,
    newsTrading: true,
    strengths: ["Pionnier du futures prop trading", "Prix bas", "Payouts rapides"],
    weaknesses: ["Futures uniquement (CME)", "Pas de crypto/forex"],
    bestFor: ["day", "futures"],
    rating: 4.6,
  },
  {
    id: "e8",
    name: "E8 Funding",
    flag: "8️⃣",
    website: "https://e8markets.com/?ref=ANATRADEX",
    price100k: 588,
    steps: "2 étapes",
    maxDD: 8,
    dailyDD: 5,
    profitTarget: [8, 5],
    split: "80%",
    payoutDays: 14,
    platforms: ["MT4", "MT5"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Programme 8-Check pour rescale", "Support réactif"],
    weaknesses: ["Split fixe à 80%", "Moins connue"],
    bestFor: ["day", "swing"],
    rating: 4.2,
  },
  {
    id: "ftuk",
    name: "FTUK",
    flag: "🇬🇧",
    website: "https://ftuk.com/?ref=ANATRADEX",
    price100k: 599,
    steps: "Instant Funding (0 étape)",
    maxDD: 10,
    dailyDD: 5,
    profitTarget: [10, 0],
    split: "80%",
    payoutDays: 30,
    platforms: ["MT4", "MT5"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Instant funding = trader direct sur compte réel", "Pas de challenge à répéter"],
    weaknesses: ["Payouts mensuels", "Split moyen"],
    bestFor: ["swing", "prop"],
    rating: 4.0,
  },
  {
    id: "fundingpips",
    name: "FundingPips",
    flag: "💧",
    website: "https://fundingpips.com/?ref=ANATRADEX",
    price100k: 449,
    steps: "2 étapes",
    maxDD: 10,
    dailyDD: 5,
    profitTarget: [8, 5],
    split: "80–90%",
    payoutDays: 7,
    platforms: ["MT4", "MT5", "cTrader"],
    consistency: false,
    weekendHolding: true,
    newsTrading: true,
    strengths: ["Payouts hebdomadaires", "Prix agressif", "Croissance rapide"],
    weaknesses: ["Historique court (2023+)"],
    bestFor: ["day", "scalp"],
    rating: 4.3,
  },
];

const STYLES = [
  { id: "all",       label: "Tous les styles" },
  { id: "scalp",     label: "Scalping" },
  { id: "day",       label: "Day trading" },
  { id: "swing",     label: "Swing trading" },
  { id: "long_term", label: "Long terme" },
  { id: "prop",      label: "Prop trader pro" },
  { id: "futures",   label: "Futures uniquement" },
];

export default function PropFirmCompare() {
  usePageMeta({
    title: "Comparateur Prop Firms 2026 · FTMO, FundedNext, The5%ers · ANATRADEX",
    description: "Comparez les meilleures prop firms de 2026 : coût, drawdown, split des profits, délais de payout. Trouvez celle qui correspond à votre style de trading.",
    og: { title: "Comparateur Prop Firms 2026 · ANATRADEX" },
  });

  const [style, setStyle] = useState("all");
  const [sortBy, setSortBy] = useState("rating");
  const [platform, setPlatform] = useState("all");
  const [affiliates, setAffiliates] = useState({});

  useEffect(() => {
    fetch(`${window.location.origin}/api/public/affiliates`)
      .then(r => r.json()).then(d => setAffiliates(d.ids || {})).catch(() => {});
  }, []);

  const withAff = (url, firmId) => {
    const code = affiliates[firmId];
    if (!code) return url;
    return url.replace("?ref=ANATRADEX", `?ref=${code}`);
  };

  const filtered = useMemo(() => {
    let list = [...FIRMS];
    if (style !== "all") list = list.filter(f => f.bestFor.includes(style));
    if (platform !== "all") list = list.filter(f => f.platforms.includes(platform));
    if (sortBy === "rating")     list.sort((a, b) => b.rating - a.rating);
    if (sortBy === "price")      list.sort((a, b) => a.price100k - b.price100k);
    if (sortBy === "split")      list.sort((a, b) => parseInt(b.split) - parseInt(a.split));
    if (sortBy === "payout")     list.sort((a, b) => a.payoutDays - b.payoutDays);
    if (sortBy === "dd")         list.sort((a, b) => b.maxDD - a.maxDD);
    return list;
  }, [style, sortBy, platform]);

  const platforms = Array.from(new Set(FIRMS.flatMap(f => f.platforms))).sort();

  return (
    <div className="min-h-screen bg-bg text-white/85" data-testid="propfirm-compare-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-lg">P</div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Outil d'Analyse Statistique</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/about" className="text-sm text-white/60 hover:text-white">À propos</Link>
            <Link to="/pricing" className="text-sm text-white/60 hover:text-white">Tarifs</Link>
            <Link to="/app" className="btn">Dashboard</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-accent-blue/80 mb-3">Comparateur 2026</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 max-w-3xl" data-testid="compare-title">
          Quelle prop firm est faite <span className="text-accent-blue">pour vous</span> ?
        </h1>
        <p className="text-white/60 max-w-3xl leading-relaxed">
          8 prop firms comparées objectivement sur les critères qui comptent vraiment : coût, drawdown maximum,
          split des profits, délais de payout, plateformes supportées, règles de consistency. Utilisez les filtres
          pour trouver celle qui correspond à votre style de trading.
        </p>
      </section>

      {/* Quiz CTA */}
      <section className="max-w-7xl mx-auto px-6 mb-6">
        <Link to="/quiz" className="block panel p-4 border-accent-blue/30 hover:border-accent-blue transition group" data-testid="compare-quiz-cta">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-accent-blue/15 border border-accent-blue/30 grid place-items-center shrink-0">
              <Sparkles size={18} className="text-accent-blue" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-sm">Vous ne savez pas laquelle choisir ?</div>
              <div className="text-xs text-white/50">Faites notre quiz en 5 questions — on vous recommande les 3 meilleures pour votre profil.</div>
            </div>
            <ExternalLink size={16} className="text-accent-blue group-hover:translate-x-1 transition" />
          </div>
        </Link>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 mb-6">
        <div className="panel p-4 flex flex-wrap items-center gap-3" data-testid="compare-filters">
          <div className="flex items-center gap-1.5 text-xs text-white/60 mr-2">
            <Filter size={13} /> Filtres :
          </div>
          <FilterSelect label="Style" value={style} onChange={setStyle} options={STYLES} testid="filter-style" />
          <FilterSelect label="Plateforme" value={platform} onChange={setPlatform}
            options={[{ id: "all", label: "Toutes" }, ...platforms.map(p => ({ id: p, label: p }))]}
            testid="filter-platform" />
          <FilterSelect label="Trier par" value={sortBy} onChange={setSortBy}
            options={[
              { id: "rating", label: "Note ⭐" },
              { id: "price",  label: "Prix ↑" },
              { id: "split",  label: "Split % ↓" },
              { id: "payout", label: "Payout rapide" },
              { id: "dd",     label: "Drawdown large" },
            ]} testid="filter-sort" />
          <div className="ml-auto text-xs text-white/40">{filtered.length} / {FIRMS.length} prop firms</div>
        </div>
      </section>

      {/* Grid of cards */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(f => <FirmCard key={f.id} firm={f} />)}
        </div>
        {filtered.length === 0 && (
          <div className="panel p-10 text-center text-white/50">
            Aucune prop firm ne correspond à ces filtres. Ajustez vos critères.
          </div>
        )}
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl font-bold mb-4">Tableau récapitulatif</h2>
        <div className="panel p-0 overflow-x-auto" data-testid="compare-table-container">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs text-white/50 uppercase tracking-wider">
              <tr>
                <Th>Prop Firm</Th>
                <Th>Prix 100k</Th>
                <Th>Étapes</Th>
                <Th>Max DD</Th>
                <Th>Daily DD</Th>
                <Th>Split</Th>
                <Th>Payout</Th>
                <Th>Note</Th>
                <Th>Site</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <tr key={f.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <Td><span className="mr-2">{f.flag}</span>{f.name}</Td>
                  <Td>${f.price100k}</Td>
                  <Td>{f.steps}</Td>
                  <Td>{f.maxDD}%</Td>
                  <Td>{f.dailyDD}%</Td>
                  <Td className="text-accent-green">{f.split}</Td>
                  <Td>{f.payoutDays}j</Td>
                  <Td>
                    <span className="inline-flex items-center gap-1">
                      <Star size={11} className="text-yellow-400 fill-yellow-400" />
                      {f.rating}
                    </span>
                  </Td>
                  <Td>
                    <a href={withAff(f.website, f.id)} target="_blank" rel="noopener noreferrer nofollow sponsored"
                       className="text-accent-blue hover:underline inline-flex items-center gap-1">
                      Voir <ExternalLink size={10} />
                    </a>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
          <strong className="text-white/60">Transparence :</strong> les liens vers les prop firms sont des liens d'affiliation.
          Cela ne modifie ni le prix pour vous, ni notre évaluation objective des services.
          Les données affichées sont sourcées publiquement et vérifiées ; elles peuvent évoluer — consultez toujours le site officiel avant achat.
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <span>© 2026 ANATRADEX — Comparateur mis à jour février 2026.</span>
          <div className="flex gap-4">
            <Link to="/about" className="hover:text-white">À propos</Link>
            <Link to="/risk-disclosure" className="hover:text-white">Risques</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-white/40">{label}</span>
      <select className="input text-xs" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 ${className}`}>{children}</td>; }

function FirmCard({ firm, withAff }) {
  const affUrl = withAff ? withAff(firm.website, firm.id) : firm.website;
  return (
    <div className="panel p-5 flex flex-col hover:border-accent-blue/40 transition" data-testid={`firm-card-${firm.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{firm.flag}</span>
          <div>
            <div className="font-heading font-semibold text-lg leading-tight">{firm.name}</div>
            <div className="flex items-center gap-1 text-[11px] text-white/50 mt-0.5">
              <Star size={10} className="text-yellow-400 fill-yellow-400" />
              {firm.rating} <span className="text-white/30">/ 5</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-lg text-accent-blue">${firm.price100k}</div>
          <div className="text-[10px] text-white/40 uppercase tracking-wider">Compte 100k</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
        <Metric icon={TrendingUp} label="Max DD" value={`${firm.maxDD}%`} />
        <Metric icon={Award} label="Split" value={firm.split} color="text-accent-green" />
        <Metric icon={DollarSign} label="Étapes" value={firm.steps} />
        <Metric icon={Clock} label="Payout" value={`${firm.payoutDays}j`} />
      </div>

      <div className="space-y-1 mb-3">
        {firm.strengths.slice(0, 3).map(s => (
          <div key={s} className="text-[11px] text-white/60 flex items-start gap-1.5">
            <CheckCircle2 size={11} className="text-accent-green mt-0.5 shrink-0" /> {s}
          </div>
        ))}
        {firm.weaknesses.slice(0, 1).map(w => (
          <div key={w} className="text-[11px] text-white/40 flex items-start gap-1.5">
            <XCircle size={11} className="text-red-400/70 mt-0.5 shrink-0" /> {w}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {firm.platforms.map(p => (
          <span key={p} className="text-[10px] px-2 py-0.5 bg-white/5 rounded-full border border-white/10 text-white/60">{p}</span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-auto">
        <Link
          to={`/prop-firms/${firm.id}`}
          className="btn btn-secondary justify-center text-xs"
          data-testid={`firm-review-${firm.id}`}
        >
          Avis détaillé
        </Link>
        <a
          href={affUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="btn btn-primary justify-center text-xs"
          data-testid={`firm-cta-${firm.id}`}
        >
          Voir <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, color = "" }) {
  return (
    <div className="bg-white/5 rounded-md px-2 py-1.5 border border-white/5">
      <div className="text-[9px] uppercase tracking-wider text-white/40 flex items-center gap-1">
        <Icon size={9} /> {label}
      </div>
      <div className={`font-mono font-semibold text-[12px] mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
